/**
 * InterviewFlow: the respondent-facing interview flow.
 *
 * Extracted from the former monolithic `App.tsx` (audit A1 / item 28). Owns
 * the respondent state machine (AppState transitions, device selection,
 * conversation/research/quote capture, resume + room-status preflight) and the
 * live-interview chrome (header logo strategy, recording chip). The route table
 * lives in `AppRouter`, the admin DB-write surface in `AdminSetupPage`.
 *
 * A persistent respondent header and one loading surface span admission,
 * automatic enrollment and canonical response loading. Text participation
 * pins the viewport and delegates its scrolling to ConversationThread.
 *
 * @module components/InterviewFlow
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile';
import { IconPhone } from '@tabler/icons-react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import {
  AppState,
  ConversationTurn,
  ResearchEvent,
  InterviewContext,
  InvitePrefillData,
  RoomAccess,
  ProductResearch,
} from '../types';

import CallScreen from './call-screen';
import SuccessScreen from './SuccessScreen';
import ClientIntro from './ClientIntro';
import BrandedLineLoader from './BrandedLineLoader';
import CreateInterviewForm from './CreateInterviewForm';
import EmbedPermissionFallback from './embed/EmbedPermissionFallback';
import { isMediaBlockedByPolicy } from '../lib/embed/permission-check';
import { createLinkHash } from '../services/linkService';
import { useAssetsReady } from '../lib/useAssetsReady';
import { LiquidEtherBackground } from './common/LiquidEtherBackground';

import { getRoomPublicInfo } from '../services/roomValidationService';
import { DEFAULT_INTERVIEW_LANGUAGE, findBestMatch } from '../lib/languages';
import { formatEstimatedDurationShort } from '../lib/estimate-duration';
import { captureException } from '../lib/telemetry/posthog/frontend';
import {
  parseEmbedUrlParams,
  type EmbedParams,
  type EmbedCollectInfo,
} from '../lib/embed/route';
import { emitToParent } from '../lib/embed/postMessage';
import { useStartCall } from '../lib/useStartCall';
import { parseConversationConfig, type ConversationExperienceConfig, type RespondentField } from '../lib/multimodal-contract';
import { getEnv } from '../lib/utils/env';
import { useRespondentConversation } from './conversation/useRespondentConversation';
import { ConversationThread, hasVisualInteraction } from './conversation/ConversationThread';
import { ConversationProgress } from './conversation/ConversationProgress';
import { forgetResponse, publicResponseId, rememberPublicResponse } from '../lib/response-recovery';
import { ConversationRestartNotice } from './conversation/ConversationRestartNotice';
import { RespondentHeader } from './conversation/RespondentHeader';
import { ConversationTheme } from './conversation/ConversationTheme';

/**
 * Shape of the JSON returned by `GET /api/room/:roomId/resume`. Only the fields
 * read while restoring session state are declared. `valid` is the discriminator:
 * when false the data fields are absent, so they are all optional here.
 */
interface ResumeSessionResponse {
  valid?: boolean;
  conversation_config?: unknown;
  response_token?: string;
  error_code?: string;
  room_id: string;
  video_enabled?: boolean;
  show_holito_referral?: boolean;
  workspace_logo_url?: string | null;
  workspace_icon_url?: string | null;
  workspace_accent_color?: string | null;
  field_count?: number;
  probing_depth?: number;
  settings?: {
    require_video?: boolean;
    enforce_language?: string | null;
    require_name?: boolean;
    require_email?: boolean;
    identity_collection?: string;
    screen_share_enabled?: boolean;
    completion_action?: 'message' | 'redirect';
    completion_message?: string | null;
    completion_redirect_url?: string | null;
  };
  context: {
    targetName: string;
    targetUrl?: string;
    goal: string;
    details?: string;
    productResearch?: ProductResearch;
    outputLanguage?: string;
    interviewLanguage?: string;
  };
  response: {
    interview_data_id: string;
    response_number: number | null;
    interviewee_name?: string;
  };
}

/**
 * Navigation state passed from `RoomJoinPage` via `navigate(..., { state })`.
 *
 * Fields that map 1:1 onto `RoomAccess` are derived from it via `Pick` so
 * there is a single source of truth for their types (audit item 28: the
 * legacy hand-rolled mirror drifted from `RoomAccess`). The remaining members
 * are either nav-only (context, validated, passcode, prefillData, flowId,
 * embedParams) or carry a deliberately different name from their `RoomAccess`
 * counterpart; each renamed field documents the `RoomAccess` source it derives
 * from so the mapping stays the only seam.
 */
export interface NavigationState
  extends Partial<
    Pick<
      RoomAccess,
      | 'sessionId'
      | 'roomId'
      | 'videoEnabled'
      | 'requireVideo'
      | 'screenShareEnabled'
      | 'workspaceId'
      | 'workspaceLogoUrl'
      | 'workspaceIconUrl'
      | 'workspaceAccentColor'
    >
  > {
  context?: InterviewContext;
  validated?: boolean;
  passcode?: string;
  /** Maps to `RoomAccess.require_name`. */
  requireName?: boolean;
  /** Maps to `RoomAccess.require_email`. */
  requireEmail?: boolean;
  /** Maps to `RoomAccess.enforce_language`. */
  enforceLanguage?: string | null;
  /** Maps to `RoomAccess.show_holito_referral`. */
  showHolitoReferral?: boolean;
  prefillData?: InvitePrefillData;
  /**
   * Campaign-automation participant token (`pt`), forwarded from RoomJoinPage.
   * Included in the create-response body so the response links back to the
   * participant and repeat invite clicks resume the same conversation.
   */
  participantToken?: string;
  /** Null on a fresh public join, then bound to its response. Absent on older entries. */
  publicResponseId?: string | null;
  /** Response selected by a personalized invitation's history entry, used only to route reloads. */
  participantResponseId?: string;
  /**
   * Public response this history entry finished. A reload of the same entry
   * keeps its acknowledgement; a fresh visit has no marker and starts anew.
   * It grants no access and stores no credential.
   */
  completedResponseId?: string;
  flowId?: string;
  /** Embed-mode parameters (parent origin) when launched via /embed/:hash. */
  embedParams?: EmbedParams | null;
  /**
   * Historical campaign mode retained for existing navigation state.
   * Current conversations collect identity through configured questions.
   */
  identityCollection?: EmbedCollectInfo;
  /** Post-call behaviour (maps to `RoomAccess.completion_action`). */
  completionAction?: 'message' | 'redirect';
  /** Custom success-screen body (maps to `RoomAccess.completion_message`). */
  completionMessage?: string | null;
  /** Redirect destination (maps to `RoomAccess.completion_redirect_url`). */
  completionRedirectUrl?: string | null;
  /** Versioned collection settings; absence keeps existing legacy responses on their original call flow. */
  conversationConfig?: ConversationExperienceConfig | null;
}

/**
 * True when another page frames this one. Browsers withhold the SameSite
 * recovery cookie from cross-site frames, so a tab pointer saved there could
 * never be redeemed by a later resume.
 */
function isFramed(): boolean {
  try {
    return window.parent !== window;
  } catch {
    return true;
  }
}

/**
 * Whether the active step must be completed on screen before a call can start.
 * Mirrors the server's call rule, so a locked call action names its real reason.
 */
function awaitsOnScreenStep(field: RespondentField | null | undefined): boolean {
  if (!field) return false;
  return field.information?.passive ? field.information.delivery === 'on_screen' : field.answerChannel === 'on_screen';
}

/** Props for the respondent interview flow. */
interface InterviewFlowProps {
  sessionId?: string;
  initialContext?: InterviewContext;
}

export const InterviewFlow: React.FC<InterviewFlowProps> = ({
  initialContext,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { roomId: urlRoomId } = useParams<{ roomId: string }>();

  // Get context from location state if navigated from RoomJoinPage
  const locationState = location.state as NavigationState | null;

  // Derive initial values from navigation state (set by RoomJoinPage)
  const navContext = initialContext || locationState?.context;
  const navRoomId = locationState?.roomId || urlRoomId || '';
  const navPasscode = locationState?.passcode || '';
  const navVideoEnabled = locationState?.videoEnabled ?? false;
  const navRequireVideo = locationState?.requireVideo ?? false;
  const navScreenShareEnabled = locationState?.screenShareEnabled ?? false;
  const navValidated = locationState?.validated === true;
  const navEnforceLanguage = locationState?.enforceLanguage ?? null;
  const navShowHolitoReferral = locationState?.showHolitoReferral ?? true;
  // Workspace branding is forwarded by RoomJoinPage via `navigate(..., { state })`.
  // We seed local state from these so the App-level header can swap in the
  // workspace's wide logo before any API calls fire on the interview page.
  const navWorkspaceLogoUrl = locationState?.workspaceLogoUrl ?? null;
  const navWorkspaceIconUrl = locationState?.workspaceIconUrl ?? null;
  const navPrefillData = locationState?.prefillData;
  const navParticipantToken = locationState?.participantToken;
  const navParticipantResponseId = locationState?.participantResponseId;
  const navPublicResponseId = locationState?.publicResponseId;
  const navCompletedResponseId = locationState?.completedResponseId;
  const navEmbedParams: EmbedParams | null = locationState?.embedParams ?? parseEmbedUrlParams(new URLSearchParams(location.search));
  // Campaign-configured post-call behaviour. Drives SuccessScreen.
  const navCompletionAction: 'message' | 'redirect' =
    locationState?.completionAction === 'redirect' ? 'redirect' : 'message';
  const navCompletionMessage: string | null = locationState?.completionMessage ?? null;
  const navCompletionRedirectUrl: string | null = locationState?.completionRedirectUrl ?? null;
  const flowIdRef = useRef(
    (locationState?.flowId as string) || navRoomId || urlRoomId || '',
  );

  // When the user lands here from a successful link/passcode validation in
  // RoomJoinPage we already have everything needed to begin enrollment
  // synchronously. Detecting that up front avoids an unnecessary room-check
  // loading phase before `initializeSession` runs.
  // The async path (resume-via-sessionStorage, or direct hot-reload of the
  // URL) still needs the loader because it depends on the resume / status
  // API responses.
  // Embedded and framed pages never keep a tab pointer: they cannot redeem the
  // recovery cookie, so a reopen starts fresh from the validated invitation.
  const embeddedContext = !!navEmbedParams || isFramed();
  const personalInvitation = !!navParticipantToken || !!navPrefillData;
  // Each personalized link owns its history entry. Only public responses share a locator across tabs.
  const savedResponseId = !urlRoomId || embeddedContext || navCompletedResponseId ? null
    : personalInvitation ? navParticipantResponseId ?? null
      : navPublicResponseId ?? publicResponseId(urlRoomId, navPublicResponseId === undefined);
  const canSkipRoomCheck =
    !!urlRoomId && !savedResponseId && navValidated && !!navContext;

  // ALL useState hooks MUST be at the top, before any early returns (React Rules of Hooks)
  const [appState, setAppState] = useState<AppState>(() => {
    if (!canSkipRoomCheck) return AppState.Setup;
    // A reload of the entry that finished its response keeps the acknowledgement.
    if (navCompletedResponseId) return AppState.Success;
    return AppState.ClientIntro;
  });
  // Embed-mode state is held alongside appState so handlers can branch
  // without prop-drilling. Updated by the resume effect when needed.
  const [embedParams] = useState<EmbedParams | null>(navEmbedParams);
  const turnstileSiteKey = getEnv('VITE_TURNSTILE_SITE_KEY', false) || '';
  const turnstileRef = useRef<TurnstileInstance>(null);
  const turnstileUsed = useRef(false);
  const turnstileOptions = useRef({ theme: 'auto', size: 'invisible', execution: 'render' } as const).current;
  /** Validation consumes its token, so enrollment owns a separate widget. */
  const getEnrollmentToken = useCallback(async (): Promise<string | undefined> => {
    if (!embedParams || !turnstileSiteKey) return undefined;
    if (!turnstileRef.current) throw new Error('Verification is unavailable');
    if (turnstileUsed.current) turnstileRef.current.reset();
    turnstileUsed.current = true;
    return turnstileRef.current.getResponsePromise();
  }, [embedParams, turnstileSiteKey]);
  const [checkingRoom, setCheckingRoom] = useState(
    () => !!urlRoomId && !canSkipRoomCheck,
  );
  const [roomError, setRoomError] = useState<string | null>(null);
  const [roomRetryAvailable, setRoomRetryAvailable] = useState(false);
  const [resumeAttempt, setResumeAttempt] = useState(0);
  const [, setConversation] = useState<ConversationTurn[]>([]);
  const [researchEvents, setResearchEvents] = useState<ResearchEvent[]>([]);
  const [selectedMicId, setSelectedMicId] = useState<string>('');
  const [selectedSpeakerId, setSelectedSpeakerId] = useState<string>('');
  const [selectedCameraId, setSelectedCameraId] = useState<string>('');
  const [videoEnabled, setVideoEnabled] = useState<boolean>(navVideoEnabled);
  const [effectiveRequireVideo, setEffectiveRequireVideo] = useState<boolean>(navRequireVideo);
  const [screenShareEnabled, setScreenShareEnabled] = useState<boolean>(navScreenShareEnabled);
  const [completionAction, setCompletionAction] = useState(navCompletionAction);
  const [completionMessage, setCompletionMessage] = useState(navCompletionMessage);
  const [completionRedirectUrl, setCompletionRedirectUrl] = useState(navCompletionRedirectUrl);
  const [userCameraEnabled, setUserCameraEnabled] = useState(true);
  const [interviewHasStarted, setInterviewHasStarted] = useState(false);
  /** Whether the Meeting-info slide-over panel inside CallScreen is open. Lifted here so the header can host the info button. */
  const [callInfoOpen, setCallInfoOpen] = useState(false);
  const [callConversationOpen, setCallConversationOpen] = useState(false);
  const automaticallyOpenedConversation = useRef(false);
  /** An explicit panel choice belongs to the respondent until the next visual step. */
  const changeCallConversationPanel = useCallback((open: boolean) => {
    automaticallyOpenedConversation.current = false;
    setCallConversationOpen(open);
  }, []);
  const [conversationConfig, setConversationConfig] = useState(() => parseConversationConfig(locationState?.conversationConfig));
  const [responseToken, setResponseToken] = useState<string | null>(null);
  const [historicalRestartRequired, setHistoricalRestartRequired] = useState(false);
  /** Saved response the resume endpoint reported complete; it can never continue. */
  const [resumedCompleteId, setResumedCompleteId] = useState<string | null>(null);
  const restartHistorical = useRef(false);
  const attemptedResumeId = useRef<string | null>(null);
  const [threadActive, setThreadActive] = useState(false);
  const startIntent = useRef<'bootstrap' | 'call'>('call');
  const bootAttempted = useRef(false);
  const entryResolved = useRef(false);
  const submissionHandled = useRef(false);
  const revealedFields = useRef(new Set<string>());
  const [effectiveEnforceLanguage, setEffectiveEnforceLanguage] = useState<string | null>(navEnforceLanguage);
  const isLanguageLocked = !!effectiveEnforceLanguage || interviewHasStarted;
  const [effectiveRoomId, setEffectiveRoomId] = useState(navRoomId);
  useEffect(() => {
    if (effectiveRoomId) {
      flowIdRef.current = effectiveRoomId;
    }
  }, [effectiveRoomId]);
  const [effectivePasscode] = useState(navPasscode);
  const [showHolitoReferral, setShowHolitoReferral] = useState(navShowHolitoReferral);
  /**
   * Workspace branding for the live interview surfaces. When a workspace logo
   * is present, the App-level header swaps the holito BETA mark for the
   * workspace's wide logo (or shows both during an active call). Initialised
   * from navigation state and refreshed when the resume API returns workspace
   * info on a hot-reload of `/interview/:roomId`.
   */
  const [workspaceLogoUrl, setWorkspaceLogoUrl] = useState<string | null>(navWorkspaceLogoUrl);
  const [, setWorkspaceIconUrl] = useState<string | null>(navWorkspaceIconUrl);
  const [workspaceAccentColor, setWorkspaceAccentColor] = useState<string | null>(locationState?.workspaceAccentColor ?? null);
  const [, setIntervieweeName] = useState(navContext?.intervieweeName || '');
  const [interviewDataId, setInterviewDataId] = useState<string | null>(null);
  const [, setResponseNumber] = useState<number | null>(null);
  const [interviewContext, setInterviewContext] = useState<InterviewContext>(() => {
    const base = navContext || {
      targetName: '',
      goal: '',
      details: '',
      interviewLanguage: DEFAULT_INTERVIEW_LANGUAGE
    };
    if (navEnforceLanguage) {
      return { ...base, interviewLanguage: navEnforceLanguage };
    }
    return base;
  });
  const hasAutoDetectedLanguage = useRef(false);
  const multimodal = conversationConfig?.version === 1;
  const respondent = useRespondentConversation({ roomId: effectiveRoomId, responseId: interviewDataId, responseToken, enabled: multimodal });
  const refreshConversation = respondent.refresh;

  // ALL useCallback hooks MUST also be before any early returns (React Rules of Hooks)
  //
  // Tracks research events in the UI. During the live session, Gemini's native googleSearch
  // provides results via onNativeResearch (with existingSummary already populated). The
  // /api/gemini/research endpoint is internal-only (worker-to-worker) and must NOT be called
  // from the browser. When called without an existingSummary, we return a fallback message
  // instead of hitting the internal endpoint.
  const handlePerformResearch = useCallback(async (query: string, type: 'primary' | 'background', existingSummary?: string): Promise<string> => {
    const newEvent: ResearchEvent = {
      id: `research-${Date.now()}-${Math.random()}`,
      query,
      status: 'searching',
      timestamp: Date.now(),
      type,
    };
    setResearchEvents(prev => [newEvent, ...prev]);

    try {
      if (existingSummary) {
        setResearchEvents(prev => prev.map(event =>
          event.id === newEvent.id ? { ...event, status: 'complete', summary: existingSummary } : event
        ));
        return existingSummary;
      }

      // The /api/gemini/research endpoint is internal-only (requires X-Internal-Secret header).
      // Research is handled server-side by Gemini's native googleSearch tool during the live
      // interview session. Return a fallback instead of making an unauthorized API call.
      const fallbackSummary = `Research requested for: "${query}". The AI interviewer will use its built-in search capabilities during the interview.`;
      setResearchEvents(prev => prev.map(event =>
        event.id === newEvent.id ? { ...event, status: 'complete', summary: fallbackSummary } : event
      ));
      return fallbackSummary;
    } catch (error) {
      console.error("Research failed:", error);
      captureException(error instanceof Error ? error : String(error), 'InterviewFlow.handlePerformResearch', { query, type });
      setResearchEvents(prev => prev.filter(event => event.id !== newEvent.id));
      return `Research failed for the query: "${query}". Please continue the conversation without this information.`;
    }
  }, []);

  const handleSetupComplete = useCallback((context: InterviewContext) => {
    setInterviewContext(context);
    if (context.intervieweeName) setIntervieweeName(context.intervieweeName);
    setAppState(AppState.ClientIntro);

    // Research is handled server-side by Gemini's native googleSearch tool during the live
    // session. No client-side research call is needed here.
  }, []);

  // Start-call preflight (anonymous response creation, double-click guard,
  // visible error surfacing) lives in useStartCall (A10).
  const getStartCallRoomId = useCallback(
    () => flowIdRef.current || urlRoomId || '',
    [urlRoomId],
  );
  const handleAnonymousResponseCreated = useCallback((id: string, num?: number, token?: string, config?: ConversationExperienceConfig | null) => {
    setInterviewDataId(id);
    if (token) {
      setResponseToken(token);
      if (effectiveRoomId && !embeddedContext && !personalInvitation) rememberPublicResponse(effectiveRoomId, id, true);
    }
    setConversationConfig(config ?? null);
    if (typeof num === 'number') {
      setResponseNumber(num);
    }
  }, [effectiveRoomId, embeddedContext, personalInvitation]);
  const handleCallStarted = useCallback(() => {
    if (startIntent.current === 'call') setAppState(AppState.InCall);
  }, []);
  /** Keep accepted invitation details available to the greeting and call UI. */
  const handleIdentityResolved = useCallback((identity: InvitePrefillData) => {
    setInterviewContext(previous => ({ ...previous,
      intervieweeName: identity.name || previous.intervieweeName,
      intervieweeEmail: identity.email || previous.intervieweeEmail,
      intervieweeJob: identity.job || previous.intervieweeJob,
      intervieweeCompany: identity.company || previous.intervieweeCompany,
      intervieweeLinkedin: identity.linkedin || previous.intervieweeLinkedin,
      intervieweeCompanyUrl: identity.company_url || previous.intervieweeCompanyUrl,
    }));
  }, []);
  const {
    startCall: handleStartCall,
    starting: startingCall,
    error: startCallError,
    canRetry: canRetryEnrollment,
    restartRequired: participantRestartRequired,
  } = useStartCall({
    getRoomId: getStartCallRoomId,
    effectiveRoomId,
    effectivePasscode,
    embedOrigin: embedParams?.embedOrigin ?? null,
    prefillData: navPrefillData,
    getTurnstileToken: getEnrollmentToken,
    interviewDataId,
    participantToken: navParticipantToken ?? null,
    onResponseCreated: handleAnonymousResponseCreated,
    onIdentityResolved: handleIdentityResolved,
    onStarted: handleCallStarted,
  });

  // Resolve the immutable first step after enrollment, before devices.
  useEffect(() => {
    if (appState !== AppState.ClientIntro || interviewDataId || bootAttempted.current) return;
    bootAttempted.current = true;
    startIntent.current = 'bootstrap';
    void handleStartCall({ restartHistorical: restartHistorical.current });
  }, [appState, interviewDataId, handleStartCall]);
  useEffect(() => {
    if (!respondent.state || entryResolved.current) return;
    entryResolved.current = true;
    setThreadActive(!effectiveRequireVideo && respondent.state.entry === 'text');
  }, [respondent.state, effectiveRequireVideo]);
  const revealKey = respondent.state?.status === 'needs_attention'
    ? `recovery:${respondent.state.answers.filter(answer => answer.status === 'failed' || answer.status === 'conflict' || answer.status === 'skipped').map(answer => answer.fieldId).join(',')}`
    : respondent.state?.canSubmit && respondent.state.signing?.status === 'required'
    ? `signing:${respondent.state.revision}`
    : hasVisualInteraction(respondent.state?.activeField ?? null) ? `field:${respondent.state?.activeField?.fieldId}` : null;
  useEffect(() => {
    if (appState !== AppState.InCall) return;
    if (!revealKey) {
      if (automaticallyOpenedConversation.current) {
        automaticallyOpenedConversation.current = false;
        setCallConversationOpen(false);
      }
      return;
    }
    if (revealedFields.current.has(revealKey)) return;
    revealedFields.current.add(revealKey);
    if (!callConversationOpen) automaticallyOpenedConversation.current = true;
    setCallInfoOpen(false);
    setCallConversationOpen(true);
  }, [appState, revealKey, callConversationOpen]);
  useEffect(() => {
    if (respondent.state?.status !== 'submitted' || submissionHandled.current || appState === AppState.InCall) return;
    submissionHandled.current = true;
    setAppState(AppState.Success);
    emitToParent('response_complete', { scope: 'full' });
  }, [respondent.state?.status, appState]);

  const handleEndCall = useCallback((finalConversation: ConversationTurn[], finalResearch: ResearchEvent[]) => {
    // Data is already saved to Supabase in real-time by the worker
    // Just update local state and show success screen
    setConversation(finalConversation);
    setResearchEvents(finalResearch);
    if (multimodal) {
      setAppState(AppState.ClientIntro);
      setThreadActive(!effectiveRequireVideo);
      void refreshConversation();
      return;
    }
    setAppState(AppState.Success);
    emitToParent('response_complete');

    if (effectiveRoomId && !embeddedContext) forgetResponse(effectiveRoomId, interviewDataId);
  }, [effectiveRoomId, interviewDataId, embeddedContext, multimodal, effectiveRequireVideo, refreshConversation]);

  // Embed-mode lifecycle: emit hollie:loaded once the iframe is interactive
  // (room check resolved, no pending error), and hollie:close on unmount so
  // the parent snippet can tear down the modal even if the user closes by
  // navigating away inside the iframe.
  useEffect(() => {
    if (!embedParams) return;
    if (checkingRoom || roomError) return;
    emitToParent('loaded');
  }, [embedParams, checkingRoom, roomError]);

  useEffect(() => {
    if (!embedParams) return undefined;
    return () => {
      emitToParent('close');
    };
  }, [embedParams]);

  // A public link's response is finished once the server accepts its submission,
  // or reports it complete when this tab tries to resume it. Participant
  // invitations resolve their own response before any tab pointer, so their
  // history marker selects the same response on reload.
  const finishedPublicResponseId = navParticipantToken ? null
    : (respondent.state?.status === 'submitted' ? interviewDataId : null) ?? resumedCompleteId;

  // Bind private entries and completion to history. Public IDs are remembered only while unfinished.
  // The response grant lives in an HttpOnly cookie.
  useEffect(() => {
    if (!effectiveRoomId) return;
    // The browser retains router history state on refresh. Bind its invitation to
    // the selected public response without storing another credential. Fresh links
    // arrive without this marker and still resolve their own participant first.
    const bindParticipant = !!interviewDataId && !!responseToken && personalInvitation
      && navParticipantResponseId !== interviewDataId;
    // A finished public response is remembered the same way, so only this entry's
    // reload keeps the acknowledgement instead of enrolling a replacement.
    const bindCompletion = !!finishedPublicResponseId && navCompletedResponseId !== finishedPublicResponseId;
    const bindPublic = !embeddedContext && !personalInvitation && !finishedPublicResponseId && !!interviewDataId
      && !!responseToken && navPublicResponseId !== interviewDataId;
    if (bindParticipant || bindPublic || bindCompletion) {
      void navigate({ pathname: location.pathname, search: location.search, hash: location.hash }, {
        replace: true, state: { ...locationState,
          ...(bindParticipant ? { participantResponseId: interviewDataId } : {}),
          ...(bindPublic ? { publicResponseId: interviewDataId } : {}),
          ...(bindCompletion ? { completedResponseId: finishedPublicResponseId } : {}) },
      });
    }
    if (embeddedContext) return;
    if (finishedPublicResponseId && !bindCompletion) {
      forgetResponse(effectiveRoomId, finishedPublicResponseId);
    } else if (interviewDataId && responseToken && !finishedPublicResponseId) {
      if (personalInvitation) {
        // Remove a legacy tab locator belonging to this personalized entry.
        forgetResponse(effectiveRoomId, interviewDataId);
      } else {
        rememberPublicResponse(effectiveRoomId, interviewDataId, false);
      }
    }
  }, [effectiveRoomId, interviewDataId, responseToken, finishedPublicResponseId, embeddedContext, personalInvitation,
    navParticipantResponseId, navPublicResponseId, navCompletedResponseId, navigate, location.pathname, location.search, location.hash, locationState]);

  // On mount: try to resume via interview_data_id, or use navigation state, or redirect
  useEffect(() => {
    if (!urlRoomId) {
      setCheckingRoom(false);
      return;
    }

    const initializeSession = async () => {
      try {
        const did = savedResponseId;
        attemptedResumeId.current = did;
        if (did) {
          // Resume: fetch full state from server
          try {
            const res = await fetch(`/api/room/${urlRoomId}/resume?did=${encodeURIComponent(did)}`, {
              credentials: 'same-origin',
              ...(responseToken ? { headers: { Authorization: `Bearer ${responseToken}` } } : {}),
            });
            if (res.status === 410) {
              const problem = await res.json() as ResumeSessionResponse;
              if (problem.error_code === 'response_complete') {
                // A finished response never continues. The pointer effect frees
                // the tab once this entry remembers the acknowledgement.
                setResumedCompleteId(did);
                setAppState(AppState.Success);
                return;
              }
            }
            if (res.status === 409) {
              const problem = await res.json() as ResumeSessionResponse;
              if (problem.error_code === 'conversation_restart_required') {
                setHistoricalRestartRequired(true);
                return;
              }
            }
            if (res.ok) {
              const data = await res.json() as ResumeSessionResponse;
              if (data.valid) {
                const snapshotConfig = parseConversationConfig(data.conversation_config);
                if (data.conversation_config != null && !snapshotConfig) {
                  setRoomError('Could not resume. Open your invitation to start again.');
                  return;
                }
                if (!snapshotConfig) {
                  // A campaign upgrade never changes the meaning of an older response.
                  setConversationConfig(null);
                  setHistoricalRestartRequired(true);
                  return;
                }
                const restoredToken = data.response_token ?? responseToken;
                if (!restoredToken) {
                  setRoomError('Could not resume. Open your invitation to start again.');
                  return;
                }
                setResponseToken(restoredToken);
                setConversationConfig(snapshotConfig);
                // Restore full state from server response
                setEffectiveRoomId(data.room_id);
                setVideoEnabled(data.video_enabled ?? false);
                setEffectiveRequireVideo(data.settings?.require_video ?? false);
                setEffectiveEnforceLanguage(data.settings?.enforce_language ?? null);
                setScreenShareEnabled(data.settings?.screen_share_enabled ?? false);
                setCompletionAction(data.settings?.completion_action ?? 'message');
                setCompletionMessage(data.settings?.completion_message ?? null);
                setCompletionRedirectUrl(data.settings?.completion_redirect_url ?? null);
                setShowHolitoReferral(data.show_holito_referral ?? true);
                // Workspace branding (resume API includes the same fields as validate)
                setWorkspaceLogoUrl(data.workspace_logo_url ?? null);
                setWorkspaceIconUrl(data.workspace_icon_url ?? null);
                setWorkspaceAccentColor(data.workspace_accent_color ?? null);
                const ctx: InterviewContext = {
                  targetName: data.context.targetName,
                  targetUrl: data.context.targetUrl,
                  goal: data.context.goal,
                  details: data.context.details || '',
                  productResearch: data.context.productResearch,
                  outputLanguage: data.context.outputLanguage,
                  interviewLanguage: data.settings?.enforce_language || data.context.interviewLanguage,
                  intervieweeName: data.response.interviewee_name || '',
                  fieldCount: data.field_count,
                  probingDepth: data.probing_depth,
                };
                setInterviewContext(ctx);

                // Restore response data
                setInterviewDataId(data.response.interview_data_id);
                setResponseNumber(data.response.response_number);
                if (data.response.interviewee_name) setIntervieweeName(data.response.interviewee_name);

                // Reuse enrolled identity and enter the restored conversation
                setInterviewHasStarted(true);
                setAppState(AppState.ClientIntro);
                setCheckingRoom(false);
                return;
              }
            }
            if (res.status >= 500 || res.status === 429) {
              if (res.status >= 500) captureException(new Error(`Response recovery returned HTTP ${res.status}`), 'InterviewFlow.resume', { roomId: urlRoomId, did });
              setRoomRetryAvailable(true);
              setRoomError('Could not restore your progress.');
            } else {
              setRoomError(res.status === 410 ? 'This response can no longer be continued.' : 'Could not resume. Open your invitation to start again.');
            }
          } catch (err) {
            captureException(err instanceof Error ? err : String(err), 'InterviewFlow.initializeSession', { roomId: urlRoomId, did });
            setRoomRetryAvailable(true);
            setRoomError('Could not restore your progress.');
          }
          // Recovery failures keep the response pointer and never enroll a replacement.
          return;
        }

        // 2. This history entry finished its response. Keep its acknowledgement
        // rather than enrolling a replacement; a fresh visit carries no marker.
        if (navCompletedResponseId) {
          setAppState(AppState.Success);
          return;
        }

        // 3. No saved response: use navigation state from RoomJoinPage.
        if (navValidated && navContext) {
          setAppState(AppState.ClientIntro);
          setCheckingRoom(false);
          return;
        }

        // 4. No navigation state - check room status and redirect to join.
        // A10: a transient 5xx surfaces as `serverError`, NOT as a missing
        // room, so respondents on a real room don't get a false dead-end.
        const info = await getRoomPublicInfo(urlRoomId);
        if (info?.serverError) {
          setRoomError('Unable to verify status. Please try again.');
        } else if (!info?.exists) {
          setRoomError('This room does not exist.');
        } else if (info.status === 'completed') {
          setRoomError('This is already complete. Thank you for your participation!');
        } else if (info.status === 'expired') {
          setRoomError('This link has expired.');
        } else if (info.status !== 'active') {
          setRoomError('This is no longer active.');
        } else {
          // Room is active but no credentials - redirect to join page
          void navigate('/', {
            state: {
              prefillRoomId: urlRoomId,
              message: 'Please enter your passcode to join.'
            }
          });
          return;
        }
      } catch (err) {
        captureException(err instanceof Error ? err : String(err), 'InterviewFlow.initializeSession', { roomId: urlRoomId });
        setRoomError('Unable to verify status. Please try again.');
      } finally {
        setCheckingRoom(false);
      }
    };

    void initializeSession();
  }, [urlRoomId, resumeAttempt]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-detect language from browser settings (only once when entering ClientIntro)
  useEffect(() => {
    if (
      appState === AppState.ClientIntro &&
      !hasAutoDetectedLanguage.current &&
      !isLanguageLocked &&
      (interviewContext.interviewLanguage || DEFAULT_INTERVIEW_LANGUAGE) === DEFAULT_INTERVIEW_LANGUAGE
    ) {
      const browserLang = navigator.language;
      const match = findBestMatch(browserLang);
      if (match && match.code !== (interviewContext.interviewLanguage || DEFAULT_INTERVIEW_LANGUAGE)) {
        console.log(`Auto-detecting language: ${browserLang} -> ${match.code}`);
        setInterviewContext(prev => ({ ...prev, interviewLanguage: match.code }));
        hasAutoDetectedLanguage.current = true;
      }
    }
  }, [appState, isLanguageLocked, interviewContext.interviewLanguage]);

  // Keep one page shell and branded loading bar while room admission, assets
  // and the canonical response resolve. Reserve the destination header space.
  const interviewAssetsReady = useAssetsReady(workspaceLogoUrl);
  const awaitingResponse = appState === AppState.ClientIntro && !participantRestartRequired
    && (!interviewDataId || (multimodal && !respondent.state)) && !respondent.error && !startCallError;
  const loading = !historicalRestartRequired && !roomError && (checkingRoom || !interviewAssetsReady || awaitingResponse);
  // Render the immutable entry immediately, before its effect remembers the
  // choice. Otherwise device setup briefly mounts on the first text snapshot.
  const showTextThread = threadActive || (!entryResolved.current && !effectiveRequireVideo && respondent.state?.entry === 'text');

  /** Render admission failures inside the same branded page shell. */
  const renderAdmissionNotice = () => {
    if (historicalRestartRequired) return <div className="w-full flex items-center justify-center">
      <ConversationRestartNotice onRestart={() => {
        if (urlRoomId) forgetResponse(urlRoomId, attemptedResumeId.current);
        restartHistorical.current = true;
        setHistoricalRestartRequired(false);
        if (navValidated && navContext) {
          setAppState(AppState.ClientIntro);
        } else void navigate('/', { state: { prefillRoomId: urlRoomId, message: 'Enter your passcode to start a new conversation.' } });
      }} />
    </div>;

    // Show error message if room is not accessible
    if (roomError) {
      return (
        <div className="w-full flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-background border border-border rounded-2xl p-8 text-center">
            <div className="w-16 h-16 bg-surface-raised rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-8 h-8 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>

            <h1 className="text-2xl font-bold text-foreground mb-4">Not Available</h1>
            <p className="text-muted-foreground mb-8">{roomError}</p>

            <button
              onClick={() => {
                if (roomRetryAvailable) {
                  setRoomError(null);
                  setRoomRetryAvailable(false);
                  setCheckingRoom(true);
                  setResumeAttempt(attempt => attempt + 1);
                } else {
                  if (urlRoomId) forgetResponse(urlRoomId, attemptedResumeId.current);
                  void navigate('/');
                }
              }}
              className="w-full py-3 button-accent font-bold rounded-lg transition-colors"
            >
              {roomRetryAvailable ? 'Try again' : 'Back to Join'}
            </button>
          </div>
        </div>
      );
    }

    return null;
  };

  const renderContent = () => {
    if (historicalRestartRequired || roomError) return renderAdmissionNotice();
    switch (appState) {
      case AppState.Setup:
        return <CreateInterviewForm onSetupComplete={handleSetupComplete} />;
      case AppState.ClientIntro: {
        if (participantRestartRequired) return <ConversationRestartNotice pending={startingCall} error={startCallError} onRestart={() => {
          startIntent.current = 'bootstrap';
          void handleStartCall({ restartHistorical: true });
        }} />;
        if (!interviewDataId || (multimodal && !respondent.state)) return (
          <div role="alert" className="w-full max-w-xl p-4 space-y-4 text-sm">
            <p>{respondent.error || startCallError}</p>
            {(interviewDataId || canRetryEnrollment) && <button type="button" className="cursor-pointer underline" onClick={() => {
              if (interviewDataId) void respondent.retry();
              else { startIntent.current = 'bootstrap'; void handleStartCall(); }
            }}>Retry</button>}
          </div>
        );
        if (multimodal && showTextThread && respondent.state) return renderThread(false);
        if (embedParams && isMediaBlockedByPolicy(videoEnabled)) return (
          <EmbedPermissionFallback hash={createLinkHash(effectiveRoomId, effectivePasscode)}
            onContinueInText={multimodal && !effectiveRequireVideo ? () => {
              entryResolved.current = true;
              setThreadActive(true);
            } : undefined} />
        );
        const durationLabel = formatEstimatedDurationShort(
          interviewContext.fieldCount ?? 0,
          interviewContext.probingDepth ?? 3,
        );
        return (
          <ClientIntro
            videoEnabled={videoEnabled}
            intervieweeName={interviewContext.intervieweeName}
            durationLabel={durationLabel}
            interviewLanguage={interviewContext.interviewLanguage || DEFAULT_INTERVIEW_LANGUAGE}
            effectiveEnforceLanguage={effectiveEnforceLanguage}
            isLanguageLocked={isLanguageLocked}
            interviewHasStarted={interviewHasStarted}
            startingCall={startingCall}
            startCallError={startCallError}
            effectiveRequireVideo={effectiveRequireVideo}
            onLanguageChange={(lang) => setInterviewContext(prev => ({ ...prev, interviewLanguage: lang }))}
            onMicrophoneSelect={setSelectedMicId}
            onSpeakerSelect={setSelectedSpeakerId}
            onCameraSelect={setSelectedCameraId}
            onCameraToggle={setUserCameraEnabled}
            onStartCall={() => {
              startIntent.current = 'call';
              void handleStartCall();
            }}
            onAnswerInText={multimodal && !effectiveRequireVideo ? () => {
              // An explicit choice wins even if the initial entry effect is pending.
              entryResolved.current = true;
              setThreadActive(true);
            } : undefined}
          />
        );
      }
      case AppState.Success:
        return <SuccessScreen
            showHolitoReferral={showHolitoReferral}
            completionAction={completionAction}
            completionMessage={completionMessage}
            completionRedirectUrl={completionRedirectUrl}
            embedOrigin={embedParams?.embedOrigin ?? null}
          />;
      default:
        return null;
    }
  };

  /** Keep the optional call action with embedded question controls. */
  const renderCallAction = () => showCallAction ? <button
              type="button" aria-label="Start call"
              title={callGuidance}
              aria-description={callGuidance}
              disabled={!callUnlocked || respondent.pending || respondent.canRetry}
              onClick={() => setThreadActive(false)}
              className="button-accent flex size-10 shrink-0 cursor-pointer items-center justify-center rounded-full disabled:cursor-not-allowed disabled:opacity-40"
            ><IconPhone size={20} aria-hidden="true" /></button> : null;

  /** Move one controlled thread between main area and panel without losing work. */
  const renderThread = (inCall: boolean) => respondent.state ? (
    <ConversationThread fileCollection={respondent.fileCollection} state={respondent.state} draft={respondent.draft} pending={respondent.pending} pendingInput={respondent.pendingInput} error={respondent.error} inCall={inCall}
      navigationActions={embedParams && !inCall ? renderCallAction() : undefined}
      visible={!inCall || callConversationOpen} canRetry={respondent.canRetry} selections={respondent.selections}
      structuredValues={respondent.structuredValues} onStructuredValueChange={respondent.setStructuredValue}
      editingField={respondent.editingField} onEditAnswer={respondent.beginAmend} onCancelEdit={respondent.cancelAmend}
      onSelectionsChange={respondent.setSelections} scrollPosition={respondent.scrollPosition} onDraftChange={respondent.setDraft}
      onSend={(text, attachment) => { void respondent.sendDraft(text, attachment); }} onAnswer={value => { void respondent.answer(value); }}
      onPresented={respondent.presented} onSkip={() => { void respondent.skip(); }}
      onSubmit={confirmation => { void respondent.submit(confirmation); }}
      onContinueInformation={fieldId => { void respondent.continueInformation(fieldId); }}
      onRetry={() => { void respondent.retry(); }} />
  ) : null;
  const textCollection = !loading && !roomError && !historicalRestartRequired && multimodal && appState === AppState.ClientIntro && showTextThread;
  const pinnedViewport = loading || appState === AppState.InCall || textCollection;
  // The entitlement-aware setting that hides the completion referral also keeps
  // every holito mark off the respondent page.
  const holitoBranding = showHolitoReferral;
  // Offer a call the plan allows when it can start now, or once the on-screen
  // step in front of the respondent is done. Other refusals have nothing to unlock.
  const callUnlocked = respondent.state?.canStartCall === true;
  const showCallAction = textCollection && conversationConfig?.callsEnabled === true && respondent.state?.status !== 'submitted'
    && (callUnlocked || (awaitsOnScreenStep(respondent.state?.activeField)
      && (conversationConfig.callEntry !== 'after_first_answer' || respondent.state?.answers.some(answer => answer.status === 'answered'))));
  const callGuidance = callUnlocked
    ? 'Speak with hollie. Your answers stay in this conversation.'
    : 'Complete this on-screen question before starting a call.';

  return (
    // During the live call we pin the shell to the visible viewport (100dvh)
    // so the fixed bottom control bar lands above the mobile browser chrome and
    // home indicator. 100vh counts the area behind the address bar and would
    // push the bar off-screen. The inline 100dvh overrides the h-screen
    // fallback where supported and is dropped (falling back to 100vh) where not.
    // Loading and text participation share the same viewport. Device setup and completion
    // keep min-h-screen so taller content can body-scroll.
    <ConversationTheme accentColor={workspaceAccentColor}
      className={`${pinnedViewport ? 'h-screen' : 'min-h-screen'} text-foreground flex flex-col font-sans overflow-hidden relative bg-background`}
      style={pinnedViewport ? { height: '100dvh' } : undefined}
    >
      {embedParams && turnstileSiteKey && <Turnstile ref={turnstileRef} siteKey={turnstileSiteKey} options={turnstileOptions} />}
      <LiquidEtherBackground isVisible={!embedParams && !loading && !roomError && !historicalRestartRequired && !textCollection && (appState === AppState.ClientIntro || appState === AppState.Success)} />
      {!embedParams && <RespondentHeader workspaceLogoUrl={loading ? undefined : workspaceLogoUrl || (navValidated || !checkingRoom ? null : undefined)} targetName={interviewContext.targetName} holitoBranding={holitoBranding}>
        {/* Shared progress sits beside the logo, outside the scrolling thread. */}
        {(textCollection || appState === AppState.InCall) && (
          <div className="flex shrink-0 items-center gap-1 sm:gap-2">
            {/* Secondary holito BETA mark when the workspace logo took the primary slot */}
            {workspaceLogoUrl && holitoBranding && appState === AppState.InCall && (
              <a
                href="https://holito.app/"
                target="_blank"
                rel="noopener noreferrer"
                className="hidden md:flex items-center gap-1.5 hover:opacity-80 transition-opacity mr-1"
                title="Powered by holito"
              >
                <img
                  src="/holito-logo-black.svg"
                  alt="holito"
                  className="h-4 w-auto opacity-70 dark:invert"
                />
                <span className="text-[9px] font-bold uppercase tracking-wider px-1 py-0.5 bg-wash-accent text-foreground rounded-lg">
                  Beta
                </span>
              </a>
            )}

            {/* Rec chip - "Rec" on mobile, "Recording" on desktop */}
            {appState === AppState.InCall ? <div
              className="flex items-center gap-2 bg-foreground/10 dark:bg-white/10 border border-foreground/20 dark:border-white/20 px-2 sm:px-3 py-1.5 rounded-full"
              role="status"
              aria-label="Recording in progress"
            >
              <span className="w-2 h-2 rounded-full bg-foreground/90 dark:bg-white/90 animate-pulse" />
              <span className="hidden sm:inline md:hidden text-foreground/90 dark:text-white/90 text-xs font-medium tracking-wide">Rec</span>
              <span className="hidden md:inline text-foreground/90 dark:text-white/90 text-xs font-medium tracking-wide">Recording</span>
            </div> : null}

            {respondent.state ? <ConversationProgress progress={respondent.state.progress} sections={conversationConfig?.sections} activeFieldId={respondent.state.activeField?.fieldId} /> : null}
            {textCollection ? renderCallAction() : null}

          </div>
        )}
      </RespondentHeader>}

      <main className={`${pinnedViewport ? 'flex-1 overflow-hidden' : 'grow'} min-h-0 flex flex-col relative z-10`} aria-busy={loading || undefined}>
        {loading && <div className="flex-1 min-h-0 flex items-center justify-center"><BrandedLineLoader logoUrl={workspaceLogoUrl || (holitoBranding ? null : undefined)}
          alt={interviewContext.targetName ? `${interviewContext.targetName} logo` : 'Workspace logo'} /></div>}
        {loading ? null : appState === AppState.InCall ? (
          <>
            {embedParams && <span role="status" aria-label="Recording in progress" className="absolute top-4 left-4 z-20 flex items-center gap-2 text-xs text-muted-foreground">
              <span className="size-2 rounded-full bg-danger" aria-hidden="true" />Recording
            </span>}
            <div className="absolute inset-0 flex flex-col">
              <CallScreen
                onEndCall={handleEndCall}
                onPerformResearch={handlePerformResearch}
                researchEvents={researchEvents}
                context={interviewContext}
                preferredDeviceId={selectedMicId}
                preferredSpeakerId={selectedSpeakerId}
                preferredCameraId={selectedCameraId}
                videoEnabled={videoEnabled}
                userCameraEnabled={userCameraEnabled}
                requireVideo={effectiveRequireVideo}
                roomId={effectiveRoomId}
                passcode={effectivePasscode}
                interviewDataId={interviewDataId ?? undefined}
                enforceLanguage={effectiveEnforceLanguage}
                screenShareEnabled={screenShareEnabled}
                infoPanelOpen={callInfoOpen}
                onInfoPanelChange={setCallInfoOpen}
                responseToken={responseToken ?? undefined}
                onConversationState={respondent.acceptState}
                conversationState={multimodal ? respondent.state : undefined}
                onInformationPresented={respondent.presented}
                conversationPanel={multimodal ? renderThread(true) : undefined}
                conversationPanelOpen={callConversationOpen}
                onConversationPanelChange={changeCallConversationPanel}
              />
            </div>
          </>
        ) : textCollection ? (
          <div className="flex-1 min-h-0 overflow-hidden flex flex-col w-full">{renderContent()}</div>
        ) : appState === AppState.Success ? (
          <div className="grow flex flex-col w-full relative">
            {renderContent()}
          </div>
        ) : (
          <div className="grow flex flex-col items-center justify-center w-full relative container mx-auto p-4 md:p-8">
            {renderContent()}
          </div>
        )}
      </main>
    </ConversationTheme>
  );
};

export default InterviewFlow;
